import {type CSSProperties, type ReactNode, useId, useState, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {useGetList} from 'src/feature/product/context/useQuery'
import {useProduct} from 'src/feature/product/hook'

/** Stroke icons from dashboard-store (24×24, currentColor). */
function Icon({
  d,
  size = 18,
  stroke = 1.6,
  fill = 'none',
  children,
  style,
}: {
  d?: string
  size?: number
  stroke?: number
  fill?: string
  children?: ReactNode
  style?: CSSProperties
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill={fill}
      stroke='currentColor'
      strokeWidth={stroke}
      strokeLinecap='round'
      strokeLinejoin='round'
      style={style}
    >
      {d ? <path d={d} /> : children}
    </svg>
  )
}

const I = {
  analytics: (p: {size?: number; stroke?: number; style?: CSSProperties}) => (
    <Icon {...p}>
      <path d='M4 20V10M10 20V4M16 20v-7M22 20H2' />
    </Icon>
  ),
  rocket: (p: {size?: number; stroke?: number; style?: CSSProperties}) => (
    <Icon {...p}>
      <path d='M5 13l4 4M14 4l6 6-9 9-3 1 1-3zM12 6L6 12l-2 5 5-2 6-6' />
    </Icon>
  ),
  gift: (p: {size?: number; stroke?: number; style?: CSSProperties}) => (
    <Icon {...p}>
      <path d='M20 12v9H4v-9M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z' />
    </Icon>
  ),
  arrow: (p: {size?: number; stroke?: number; style?: CSSProperties}) => (
    <Icon {...p}>
      <path d='M5 12h14M13 6l6 6-6 6' />
    </Icon>
  ),
  question: (p: {size?: number; stroke?: number; style?: CSSProperties}) => (
    <Icon {...p}>
      <circle cx='12' cy='12' r='9' />
      <path d='M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .9-1 1.7M12 17h0' />
    </Icon>
  ),
  cake: (p: {size?: number; stroke?: number; style?: CSSProperties}) => (
    <Icon {...p}>
      <path d='M3 21V13h18v8M3 17c2 0 2-2 4.5-2s2.5 2 4.5 2 2-2 4.5-2 2.5 2 4.5 2M12 13V7M12 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4z' />
    </Icon>
  ),
  pair: (p: {size?: number; stroke?: number; style?: CSSProperties}) => (
    <Icon {...p}>
      <circle cx='9' cy='9' r='3' />
      <circle cx='17' cy='11' r='2.5' />
      <path d='M3 19c0-3 3-5 6-5s6 2 6 5M14 19c0-2 2-3.5 4-3.5s3 1.5 3 3.5' />
    </Icon>
  ),
  refresh: (p: {size?: number; stroke?: number; style?: CSSProperties}) => (
    <Icon {...p}>
      <path d='M3 12a9 9 0 0 1 15-7l3 3M21 4v5h-5M21 12a9 9 0 0 1-15 7l-3-3M3 20v-5h5' />
    </Icon>
  ),
  chev: (p: {size?: number; stroke?: number; style?: CSSProperties}) => (
    <Icon {...p}>
      <path d='M6 9l6 6 6-6' />
    </Icon>
  ),
  info: (p: {size?: number; stroke?: number; style?: CSSProperties}) => (
    <Icon {...p}>
      <circle cx='12' cy='12' r='9' />
      <path d='M12 11v5M12 8h0' />
    </Icon>
  ),
  arrowUp: (p: {size?: number; stroke?: number; style?: CSSProperties}) => (
    <Icon {...p}>
      <path d='M7 17L17 7M9 7h8v8' />
    </Icon>
  ),
  arrowDown: (p: {size?: number; stroke?: number; style?: CSSProperties}) => (
    <Icon {...p}>
      <path d='M7 7l10 10M9 17h8V9' />
    </Icon>
  ),
  spark: (p: {size?: number; stroke?: number; style?: CSSProperties}) => (
    <Icon {...p}>
      <path d='M12 3l2 5 5 2-5 2-2 5-2-5-5-2 5-2zM19 14l1 2 2 1-2 1-1 2-1-2-2-1 2-1z' />
    </Icon>
  ),
  check: (p: {size?: number; stroke?: number; style?: CSSProperties}) => (
    <Icon {...p}>
      <path d='M5 12l5 5L20 7' />
    </Icon>
  ),
  bulb: (p: {size?: number; stroke?: number; style?: CSSProperties}) => (
    <Icon {...p}>
      <path d='M9 18h6M10 22h4M9 14a5 5 0 1 1 6 0c-1 .8-1 2-1 3H10c0-1 0-2.2-1-3z' />
    </Icon>
  ),
  close: (p: {size?: number; stroke?: number; style?: CSSProperties}) => (
    <Icon {...p}>
      <path d='M6 6l12 12M18 6L6 18' />
    </Icon>
  ),
}

function ProgressBar({
  value,
  total,
  color = 'var(--dsm-primary)',
  height = 6,
}: {
  value: number
  total: number
  color?: string
  height?: number
}) {
  const pct = Math.min(100, Math.max(0, (value / total) * 100))
  return (
    <div
      style={{
        height,
        background: 'var(--dsm-line-2)',
        borderRadius: 999,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: `${pct}%`,
          height: '100%',
          background: color,
          borderRadius: 999,
          transition: 'width 0.6s cubic-bezier(.2,.8,.2,1)',
        }}
      />
    </div>
  )
}

function Sparkline({
  data,
  width = 120,
  height = 36,
  color = 'currentColor',
  fill = true,
  strokeWidth = 2,
}: {
  data: number[]
  width?: number | string
  height?: number
  color?: string
  fill?: boolean
  strokeWidth?: number
}) {
  const uid = useId().replace(/:/g, '')
  const min = Math.min(...data)
  const max = Math.max(...data)
  const range = max - min || 1
  const responsive = typeof width === 'string'
  const vbW = responsive ? 120 : width
  const step = data.length > 1 ? vbW / (data.length - 1) : 0
  const pts = data.map(
    (v, i) => [i * step, height - ((v - min) / range) * (height - 4) - 2] as const
  )
  const path = pts.map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`)).join(' ')
  const area = `${path} L${vbW},${height} L0,${height} Z`
  const last = pts[pts.length - 1]

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${vbW} ${height}`}
      preserveAspectRatio='none'
      style={{overflow: 'visible', display: 'block'}}
    >
      {fill && (
        <>
          <defs>
            <linearGradient id={`dsm-spark-g${uid}`} x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor={color} stopOpacity={0.18} />
              <stop offset='100%' stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <path d={area} fill={`url(#dsm-spark-g${uid})`} stroke='none' />
        </>
      )}
      <path
        d={path}
        fill='none'
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <circle cx={last[0]} cy={last[1]} r={2.5} fill={color} />
    </svg>
  )
}

function Card({
  children,
  style,
  className,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: {
  children: ReactNode
  style?: CSSProperties
  className?: string
  onClick?: () => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}) {
  return (
    <div
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={className}
      style={{
        background: 'var(--dsm-panel)',
        borderRadius: 16,
        border: '1px solid var(--dsm-line)',
        boxShadow: 'var(--dsm-shadow)',
        ...style,
      }}
    >
      {children}
    </div>
  )
}

function SectionTitle({
  eyebrow,
  title,
  action,
  hint,
}: {
  eyebrow?: string
  title: string
  action?: ReactNode
  hint?: string
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        margin: '24px 0 14px',
        flexWrap: 'wrap',
        gap: 12,
      }}
    >
      <div>
        {eyebrow && (
          <div
            style={{
              fontSize: 11,
              letterSpacing: 1.4,
              fontWeight: 600,
              color: 'var(--dsm-ink-3)',
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            {eyebrow}
          </div>
        )}
        <div style={{fontSize: 18, fontWeight: 700, color: 'var(--dsm-ink)', letterSpacing: -0.2}}>
          {title}
        </div>
        {hint && <div style={{fontSize: 13, color: 'var(--dsm-ink-2)', marginTop: 4}}>{hint}</div>}
      </div>
      {action}
    </div>
  )
}

function TodaySection({density}: {density: 'compact' | 'comfy'}) {
  const [insightIndex, setInsightIndex] = useState(0)

  const metrics = [
    {
      label: 'Loyalty members',
      value: '1,240',
      delta: '+12%',
      trend: 'up' as const,
      spark: [10, 14, 12, 16, 18, 22, 24],
      color: 'var(--dsm-primary)',
    },
    {
      label: 'Active loyalty members',
      value: '856',
      delta: '+4 pts',
      trend: 'up' as const,
      spark: [55, 58, 60, 62, 65, 67, 68],
      color: 'var(--dsm-info)',
    },
    {
      label: 'New members',
      value: '23',
      delta: '+6',
      trend: 'up' as const,
      spark: [8, 12, 11, 14, 18, 20, 23],
      color: 'var(--dsm-good)',
    },
    {
      label: 'Points redeemed',
      value: '12,450',
      delta: '−3%',
      trend: 'down' as const,
      spark: [50, 48, 46, 45, 44, 42, 41],
      color: 'var(--dsm-warn)',
    },
  ]

  const insights = [
    {
      title: (
        <>
          Your busiest hours are <span style={{color: 'var(--dsm-primary)'}}>5–6 PM</span> on
          weekdays and <span style={{color: 'var(--dsm-primary)'}}>2–4 PM</span> on weekends.
        </>
      ),
      subtext: '8 products generate 80% of total sales.',
    },
    {
      title: (
        <>
          Customers visit every <span style={{color: 'var(--dsm-primary)'}}>14 days</span>.
        </>
      ),
      subtext: 'Repeat customers contribute to 65% of your monthly growth.',
    },
  ]

  useEffect(() => {
    const timer = setInterval(() => {
      setInsightIndex((prev) => (prev + 1) % insights.length)
    }, 5000)
    return () => clearInterval(timer)
  }, [insights.length])

  const pad = density === 'comfy' ? '8px 22px' : '6px 16px'
  const cardPad = density === 'comfy' ? 24 : 18

  return (
    <section data-screen-label='01 Today'>
      <SectionTitle
        eyebrow='Today · Tue, May 8'
        title='Snapshot'
        action={
          <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
            <button type='button' className='dsm-ghost-btn'>
              <I.refresh size={14} /> Refresh
            </button>
            <button type='button' className='dsm-ghost-btn'>
              Last 7 days <I.chev size={14} />
            </button>
          </div>
        }
      />
      <Card style={{padding: cardPad, overflow: 'hidden'}}>
        <div
          className='dsm-snapshot-grid'
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: 0,
          }}
        >
          {metrics.map((m, i) => (
            <div
              key={m.label}
              style={{
                padding: pad,
                borderRight: i < metrics.length - 1 ? '1px solid var(--dsm-line-2)' : 'none',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  color: 'var(--dsm-ink-2)',
                  fontSize: 12.5,
                  fontWeight: 500,
                }}
              >
                {m.label}
                <I.info size={12} style={{color: 'var(--dsm-ink-3)'}} />
              </div>
              <div style={{display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap'}}>
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 700,
                    letterSpacing: -0.8,
                    color: 'var(--dsm-ink)',
                    lineHeight: 1,
                  }}
                >
                  {m.value}
                </div>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 2,
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: m.trend === 'up' ? 'var(--dsm-good)' : 'var(--dsm-warn)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {m.trend === 'up' ? <I.arrowUp size={11} /> : <I.arrowDown size={11} />}
                  {m.delta}
                </div>
              </div>
              <div style={{color: m.color, marginTop: 2}}>
                <Sparkline data={m.spark} width='100%' height={28} color={m.color} />
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            marginTop: 18,
            background: 'linear-gradient(90deg, var(--dsm-primary-soft), transparent 70%)',
            border: '1px solid var(--dsm-primary-soft)',
            borderRadius: 12,
            padding: '14px 18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 14,
            flexWrap: 'wrap',
          }}
        >
          <div style={{display: 'flex', alignItems: 'center', gap: 14, minWidth: 0}}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                flexShrink: 0,
                background: 'var(--dsm-primary)',
                color: 'white',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <I.spark size={18} />
            </div>
            <div style={{minWidth: 0}}>
              <div
                key={insightIndex}
                style={{
                  animation: 'dsm-fade-in 0.5s ease-out',
                }}
              >
                <div style={{fontSize: 14, fontWeight: 600, color: 'var(--dsm-ink)'}}>
                  {insights[insightIndex].title}
                </div>
                <div style={{fontSize: 12, color: 'var(--dsm-ink-2)', marginTop: 2}}>
                  {insights[insightIndex].subtext}
                </div>
              </div>
            </div>
          </div>
          <button type='button' className='dsm-link-btn'>
            See what&apos;s driving sales <I.arrow size={14} />
          </button>
        </div>
      </Card>
    </section>
  )
}

type SetupTaskId =
  | 'products'
  | 'referrals'
  | 'signup'
  | 'birthday'
  | 'vip'
  | 'business'
  | 'users'
  | 'performance'
  | 'seasonal'
  | 'points_campaign'

function SetupSection({onFinishSetup}: {onFinishSetup: () => void}) {
  const navigate = useNavigate()
  const [done, setDone] = useState<Record<string, boolean>>({
    products: false,
    referrals: false,
    signup: false,
    birthday: false,
    vip: false,
    business: false,
    users: false,
    performance: false,
    seasonal: false,
    points_campaign: false,
  })

  const initialItems = [
    {id: 'products', label: 'Upload products', meta: 'Catalogue & pricing', hasArrow: true},
    {id: 'referrals', label: 'Enable referrals', meta: 'Reward both parties'},
    {id: 'signup', label: 'Launch sign up reward', meta: 'First-visit incentive'},
    {id: 'birthday', label: 'Create birthday campaign', meta: 'Automated rewards'},
    {id: 'vip', label: 'Set VIP rules & offers', meta: 'Top-tier perks'},
    {id: 'business', label: 'Add business details', meta: 'Name, address, hours'},
    {id: 'users', label: 'Add users', meta: 'Team access'},
  ]

  const growthItems = [
    {id: 'performance', label: 'Check performance', meta: 'Review your stats'},
    {id: 'seasonal', label: 'Create new seasonal offer', meta: 'Drive seasonal sales'},
    {
      id: 'points_campaign',
      label: 'Launch a limited time, product-based points campaign',
      meta: 'Boost specific items',
    },
  ]

  const isInitialDone = initialItems.every((i) => done[i.id])
  const items = isInitialDone ? growthItems : initialItems
  const title = isInitialDone ? 'Growth checklist' : 'Complete your set up'

  const total = items.length
  const completed = items.filter((i) => done[i.id]).length

  const toggle = (id: string) => {
    if (id === 'products') {
      navigate('/products')
    }
    setDone((d) => ({...d, [id]: !d[id]}))
  }

  const {isLoading} = useGetList()
  const products = useProduct()
  return (
    <Card style={{padding: 20, height: '100%', display: 'flex', flexDirection: 'column'}}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 14,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              letterSpacing: 1.4,
              fontWeight: 600,
              color: 'var(--dsm-ink-3)',
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            {isInitialDone ? 'Boost growth' : 'Get ready'}
          </div>
          <div style={{fontSize: 16, fontWeight: 700, color: 'var(--dsm-ink)'}}>{title}</div>
        </div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--dsm-primary)',
            background: 'var(--dsm-primary-soft)',
            padding: '4px 10px',
            borderRadius: 999,
          }}
        >
          {completed}/{total} complete
        </div>
      </div>

      <div style={{marginBottom: 14}}>
        <ProgressBar value={completed} total={total} />
      </div>

      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          flex: 1,
        }}
      >
        {items.map((it) => {
          const isDone = done[it.id]
          return (
            <li key={it.id}>
              <button
                type='button'
                className='dsm-setup-task'
                onClick={() => toggle(it.id)}
                style={{
                  width: '100%',
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 8px',
                  margin: '0 -8px',
                  background: 'none',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                }}
              >
                <span
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 6,
                    border: isDone ? 'none' : '1.5px solid var(--dsm-line)',
                    background: isDone ? 'var(--dsm-primary)' : 'var(--dsm-panel)',
                    display: 'grid',
                    placeItems: 'center',
                    color: 'white',
                    flexShrink: 0,
                    transition: 'all .2s',
                  }}
                >
                  {isDone && <I.check size={12} stroke={3} />}
                </span>
                <span style={{minWidth: 0}}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: isDone ? 'var(--dsm-ink-3)' : 'var(--dsm-ink)',
                      textDecoration: isDone ? 'line-through' : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    {it.label}{' '}
                    {(it as any).hasArrow && (
                      <I.arrow size={12} style={{color: 'var(--dsm-ink-3)'}} />
                    )}
                  </div>
                  <div style={{fontSize: 11, color: 'var(--dsm-ink-3)', marginTop: 1}}>
                    {it.meta}
                  </div>
                </span>
                <I.question size={13} style={{color: 'var(--dsm-ink-3)', flexShrink: 0}} />
              </button>
            </li>
          )
        })}
      </ul>

      <button
        type='button'
        className='dsm-primary-btn'
        style={{marginTop: 14}}
        onClick={onFinishSetup}
      >
        {isInitialDone ? 'Start marketing' : 'Finish setup faster'} <I.arrow size={14} />
      </button>
    </Card>
  )
}

function GrowSection({
  onNavigateHelp,
  onDismiss,
}: {
  onNavigateHelp: () => void
  onDismiss: () => void
}) {
  return (
    <Card
      style={{
        padding: 0,
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(160deg, oklch(20% 0.06 300) 0%, oklch(28% 0.10 305) 100%)',
        color: 'white',
        border: '1px solid oklch(28% 0.10 305)',
      }}
    >
      <div style={{padding: 20, flex: 1, display: 'flex', flexDirection: 'column'}}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 18,
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 11,
              letterSpacing: 1.4,
              fontWeight: 600,
              textTransform: 'uppercase',
              color: 'oklch(85% 0.10 300)',
            }}
          >
            <I.rocket size={12} /> Help Center
          </span>
          {/* <button
            type='button'
            className='dsm-grow-dismiss'
            onClick={onDismiss}
            aria-label='Dismiss'
          >
            <I.close size={14} />
          </button> */}
        </div>
        <div style={{flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
          <div
            style={{
              fontSize: 22,
              fontWeight: 600,
              lineHeight: 1.25,
              letterSpacing: -0.4,
            }}
          >
            Customers return <span style={{color: 'oklch(85% 0.14 90)'}}>3.2× more</span> when
            rewards unlock sooner.
          </div>
          <div style={{fontSize: 13, color: 'oklch(82% 0.05 300)', marginTop: 10, lineHeight: 1.5}}>
            Based on 1,200+ merchants like yours. Try a 50-point reward at signup.
          </div>
          <div
            style={{
              marginTop: 18,
              padding: 12,
              borderRadius: 10,
              background: 'oklch(100% 0 0 / 0.06)',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 12,
            }}
          >
            <div style={{flex: 1}}>
              <div style={{color: 'oklch(80% 0.04 300)'}}>Your current threshold</div>
              <div style={{fontWeight: 600, fontSize: 14, marginTop: 2}}>
                200 points · ~$40 spend
              </div>
            </div>
            <I.arrow size={14} style={{color: 'oklch(80% 0.04 300)'}} />
            <div style={{flex: 1, textAlign: 'right'}}>
              <div style={{color: 'oklch(85% 0.14 90)'}}>Suggested</div>
              <div style={{fontWeight: 600, fontSize: 14, marginTop: 2}}>
                50 points · ~$10 spend
              </div>
            </div>
          </div>
          <div style={{fontSize: 12, color: 'oklch(82% 0.05 300)', marginTop: 12, opacity: 0.8}}>
            Tips and best practices to get more from your loyalty program.
          </div>
        </div>
        <div style={{display: 'flex', gap: 8, marginTop: 18, flexWrap: 'wrap'}}>
          <button
            type='button'
            className='dsm-primary-btn dsm-grow-primary-light'
            style={{flex: 1, minWidth: 140}}
            onClick={onNavigateHelp}
          >
            Go to Knowledge Hub
          </button>
        </div>
      </div>
    </Card>
  )
}

type ActionCardProps = {
  icon: ReactNode
  color: {bg: string; fg: string}
  title: string
  meta: string
  cta: string
  urgent?: boolean
  onClick?: () => void
}

function ActionCard({icon, color, title, meta, cta, urgent, onClick}: ActionCardProps) {
  const [hover, setHover] = useState(false)
  return (
    <Card
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: 20,
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        transform: hover ? 'translateY(-2px)' : 'none',
        boxShadow: hover ? '0 8px 24px rgba(20,14,50,0.08)' : 'var(--dsm-shadow)',
        transition: 'transform .2s, box-shadow .2s',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        minHeight: 168,
      }}
    >
      <div style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between'}}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 11,
            background: color.bg,
            color: color.fg,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {icon}
        </div>
        <button
          type='button'
          className='dsm-icon-btn'
          onClick={(e) => e.stopPropagation()}
          aria-label='Help'
        >
          <I.question size={14} />
        </button>
      </div>
      <div style={{flex: 1}}>
        <div style={{fontSize: 16, fontWeight: 600, color: 'var(--dsm-ink)', letterSpacing: -0.1}}>
          {title}
        </div>
        <div style={{fontSize: 13, color: 'var(--dsm-ink-2)', marginTop: 6, lineHeight: 1.4}}>
          {meta}
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
            fontWeight: 600,
            color: urgent ? 'var(--dsm-warn)' : 'var(--dsm-primary)',
          }}
        >
          {cta}
          <I.arrow
            size={14}
            style={{transition: 'transform .2s', transform: hover ? 'translateX(2px)' : 'none'}}
          />
        </div>
        {urgent && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--dsm-warn)',
              background: 'var(--dsm-warn-soft)',
              padding: '3px 8px',
              borderRadius: 999,
            }}
          >
            Recommended
          </span>
        )}
      </div>
    </Card>
  )
}

function ActionsSection({
  onNavigateCampaign,
  onNavigateOffer,
  onNavigateAnalytics,
}: {
  onNavigateCampaign: () => void
  onNavigateOffer: () => void
  onNavigateAnalytics: () => void
}) {
  const actions: ActionCardProps[] = [
    {
      icon: <I.rocket size={20} />,
      color: {bg: 'oklch(96% 0.04 15)', fg: 'oklch(55% 0.18 15)'},
      title: 'Bring customers back',
      meta: "Run a limited-time offer to bring back customers who haven't visited recently.",
      cta: 'Create win-back offer',
      urgent: true,
      onClick: onNavigateCampaign,
    },
    {
      icon: <I.gift size={20} />,
      color: {bg: 'var(--dsm-primary-soft)', fg: 'var(--dsm-primary)'},
      title: 'Get more repeat visits',
      meta: 'Reward loyal customers with offers on your best sellers to keep them coming back.',
      cta: 'Launch offer',
      onClick: onNavigateOffer,
    },
    {
      icon: <I.analytics size={20} />,
      color: {bg: 'var(--dsm-good-soft)', fg: 'var(--dsm-good)'},
      title: 'Check program performance',
      meta: "See what's working and where to improve to grow your customer base.",
      cta: "See what's driving sales",
      onClick: onNavigateAnalytics,
    },
  ]

  return (
    <section data-screen-label='02 Actions'>
      <SectionTitle
        eyebrow='TAKE ACTION'
        title='Grow your business'
        hint='Create offers, bring customers back, and track performance'
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 14,
        }}
        className='dsm-actions-grid'
      >
        {actions.map((a) => (
          <ActionCard key={a.title} {...a} />
        ))}
      </div>
    </section>
  )
}

type CampaignFilter = 'active' | 'draft' | 'inactive'

type CampaignRowProps = {
  icon: ReactNode
  color: {bg: string; fg: string}
  name: string
  type: string
  status: string
  statusColor: string
  statusBg: string
  value: number
  total: number
  label: string
  expiryDays?: number
  expiryDate?: string
  totalBenefits: number
  onView: () => void
}

function CampaignRow({
  icon,
  color,
  name,
  type,
  status,
  statusColor,
  statusBg,
  value,
  total,
  label,
  expiryDays,
  expiryDate,
  totalBenefits,
  onView,
}: CampaignRowProps) {
  const [hover, setHover] = useState(false)
  const isExpiringSoon = expiryDays !== undefined && expiryDays <= 7

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '16px 20px',
        borderTop: '1px solid var(--dsm-line-2)',
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto auto',
        gap: 18,
        alignItems: 'center',
        background: hover ? 'oklch(99% 0.005 300)' : 'transparent',
        transition: 'background .15s',
      }}
    >
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: 10,
          background: color.bg,
          color: color.fg,
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{minWidth: 0}}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 6,
            flexWrap: 'wrap',
          }}
        >
          <span style={{fontSize: 14, fontWeight: 600, color: 'var(--dsm-ink)'}}>
            {name} <span style={{color: 'var(--dsm-ink-3)', fontWeight: 400}}>[{type}]</span>
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: statusColor,
              background: statusBg,
              padding: '2px 8px',
              borderRadius: 999,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <span style={{width: 5, height: 5, borderRadius: 999, background: statusColor}} />
            {status}
          </span>
          {isExpiringSoon && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--dsm-warn)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <I.refresh size={11} /> Expiring in {expiryDays} days
            </span>
          )}
        </div>
        <div style={{display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap'}}>
          {/* Progress and redemption counts removed as per user request */}
        </div>
      </div>
      <div style={{fontSize: 12, color: 'var(--dsm-ink-2)', textAlign: 'right', fontWeight: 500}}>
        {expiryDate ? `Expires: ${expiryDate}` : ''}
      </div>
      <button type='button' className='dsm-ghost-btn-sm' onClick={onView}>
        View
      </button>
    </div>
  )
}

function CampaignsSection({
  onViewCampaign,
  onViewAllCampaigns,
}: {
  onViewCampaign: () => void
  onViewAllCampaigns: () => void
}) {
  const [filter, setFilter] = useState<CampaignFilter>('active')
  const all: (Omit<CampaignRowProps, 'onView'> & {filterType: CampaignFilter})[] = [
    {
      icon: <I.gift size={18} />,
      color: {bg: 'var(--dsm-primary-soft)', fg: 'var(--dsm-primary)'},
      name: 'Spring Offer',
      type: 'Discount',
      status: 'Active',
      statusColor: 'var(--dsm-good)',
      statusBg: 'var(--dsm-good-soft)',
      value: 32,
      total: 50,
      label: 'redemptions',
      expiryDays: 3,
      expiryDate: 'May 20, 2026',
      totalBenefits: 145,
      filterType: 'active',
    },
    {
      icon: <I.pair size={18} />,
      color: {bg: 'var(--dsm-info-soft)', fg: 'var(--dsm-info)'},
      name: 'Referral Program',
      type: 'Referral',
      status: 'Active',
      statusColor: 'var(--dsm-good)',
      statusBg: 'var(--dsm-good-soft)',
      value: 18,
      total: 30,
      label: 'new customers',
      expiryDate: 'Jun 12, 2026',
      totalBenefits: 82,
      filterType: 'active',
    },
    {
      icon: <I.cake size={18} />,
      color: {bg: 'oklch(96% 0.04 15)', fg: 'oklch(60% 0.18 15)'},
      name: 'Birthday Reward',
      type: 'Birthday',
      status: 'Active',
      statusColor: 'var(--dsm-good)',
      statusBg: 'var(--dsm-good-soft)',
      value: 12,
      total: 80,
      label: 'used this week',
      totalBenefits: 310,
      filterType: 'active',
    },
    {
      icon: <I.bulb size={18} />,
      color: {bg: 'var(--dsm-warn-soft)', fg: 'var(--dsm-warn)'},
      name: 'Summer Prep',
      type: 'Discount',
      status: 'Draft',
      statusColor: 'var(--dsm-warn)',
      statusBg: 'var(--dsm-warn-soft)',
      value: 0,
      total: 100,
      label: 'planned',
      totalBenefits: 0,
      filterType: 'draft',
    },
    {
      icon: <I.close size={18} />,
      color: {bg: 'oklch(96% 0.04 15)', fg: 'oklch(55% 0.18 15)'},
      name: 'Old Winter Sale',
      type: 'Discount',
      status: 'Inactive',
      statusColor: 'oklch(55% 0.18 15)',
      statusBg: 'oklch(96% 0.04 15)',
      value: 100,
      total: 100,
      label: 'completed',
      totalBenefits: 450,
      filterType: 'inactive',
    },
  ]

  const items = all.filter((a) => a.filterType === filter)
  const filterLabel = filter.charAt(0).toUpperCase() + filter.slice(1)

  return (
    <section data-screen-label='03 Campaigns'>
      <SectionTitle
        eyebrow='Live'
        title={`${filterLabel} campaigns`}
        action={
          <div
            style={{
              display: 'flex',
              gap: 4,
              padding: 4,
              background: 'var(--dsm-panel)',
              border: '1px solid var(--dsm-line)',
              borderRadius: 10,
              flexWrap: 'wrap',
            }}
          >
            {(
              [
                {id: 'active' as const, label: 'Active'},
                {id: 'draft' as const, label: 'Draft'},
                {id: 'inactive' as const, label: 'Inactive'},
              ] as const
            ).map((t) => (
              <button
                key={t.id}
                type='button'
                onClick={() => setFilter(t.id)}
                style={{
                  border: 'none',
                  background: filter === t.id ? 'var(--dsm-ink)' : 'transparent',
                  color: filter === t.id ? 'white' : 'var(--dsm-ink-2)',
                  padding: '6px 10px',
                  borderRadius: 7,
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        }
      />
      <Card style={{overflow: 'hidden', padding: 0}}>
        {items.length === 0 ? (
          <div style={{padding: 40, textAlign: 'center', color: 'var(--dsm-ink-3)', fontSize: 13}}>
            No campaigns in this view.
          </div>
        ) : (
          items.map((c) => <CampaignRow key={c.name} {...c} onView={onViewCampaign} />)
        )}
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid var(--dsm-line-2)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
            background: 'oklch(99% 0.003 300)',
          }}
        >
          <span style={{fontSize: 12, color: 'var(--dsm-ink-3)'}}>
            3 active · 12 total this quarter
          </span>
          <button type='button' className='dsm-link-btn' onClick={onViewAllCampaigns}>
            View all campaigns <I.arrow size={13} />
          </button>
        </div>
      </Card>
    </section>
  )
}

const wrapperStyle: CSSProperties = {
  ['--dsm-bg' as string]: '#f4f4f7',
  ['--dsm-panel' as string]: '#ffffff',
  ['--dsm-ink' as string]: '#1a1530',
  ['--dsm-ink-2' as string]: '#524d66',
  ['--dsm-ink-3' as string]: '#8c869c',
  ['--dsm-line' as string]: '#e8e6ee',
  ['--dsm-line-2' as string]: '#f0eef5',
  ['--dsm-primary' as string]: 'oklch(48% 0.18 300)',
  ['--dsm-primary-soft' as string]: 'oklch(95% 0.04 300)',
  ['--dsm-primary-mid' as string]: 'oklch(65% 0.16 300)',
  ['--dsm-good' as string]: 'oklch(58% 0.13 155)',
  ['--dsm-good-soft' as string]: 'oklch(95% 0.05 155)',
  ['--dsm-warn' as string]: 'oklch(72% 0.14 70)',
  ['--dsm-warn-soft' as string]: 'oklch(96% 0.06 80)',
  ['--dsm-info' as string]: 'oklch(60% 0.13 250)',
  ['--dsm-info-soft' as string]: 'oklch(96% 0.04 250)',
  ['--dsm-shadow' as string]: '0 1px 2px rgba(20, 14, 50, 0.04), 0 4px 14px rgba(20, 14, 50, 0.04)',
  width: '100%',
  minWidth: 0,
  fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  WebkitFontSmoothing: 'antialiased',
}

/**
 * Dashboard-store style blocks: Snapshot, actions, campaigns, setup, grow tip.
 */
export function DashboardStoreMiddle() {
  const navigate = useNavigate()
  const [showGrow, setShowGrow] = useState(true)

  return (
    <div className='dashboard-store-middle' style={wrapperStyle}>
      <style>{`
        .dashboard-store-middle .dsm-ghost-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 7px 12px;
          background: var(--dsm-panel);
          border: 1px solid var(--dsm-line);
          border-radius: 8px;
          font-size: 12px;
          font-weight: 500;
          color: var(--dsm-ink-2);
          cursor: pointer;
          font-family: inherit;
          transition: background .15s, border-color .15s;
        }
        .dashboard-store-middle .dsm-ghost-btn:hover {
          background: var(--dsm-bg);
          border-color: var(--dsm-ink-3);
        }
        .dashboard-store-middle .dsm-primary-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 10px 16px;
          background: var(--dsm-primary);
          border: none;
          border-radius: 9px;
          font-size: 13px;
          font-weight: 600;
          color: white;
          cursor: pointer;
          font-family: inherit;
          transition: filter .15s;
        }
        .dashboard-store-middle .dsm-primary-btn:hover {
          filter: brightness(1.1);
        }
        .dashboard-store-middle .dsm-grow-primary-light {
          background: white !important;
          color: oklch(20% 0.06 300) !important;
        }
        .dashboard-store-middle .dsm-grow-primary-light:hover {
          filter: brightness(1.05);
        }
        .dashboard-store-middle .dsm-setup-task:hover {
          background: var(--dsm-line-2);
        }
        .dashboard-store-middle .dsm-grow-dismiss {
          width: 28px;
          height: 28px;
          padding: 0;
          background: transparent;
          border: 1px solid transparent;
          border-radius: 8px;
          color: oklch(80% 0.05 300);
          cursor: pointer;
          display: grid;
          place-items: center;
        }
        .dashboard-store-middle .dsm-grow-dismiss:hover {
          background: oklch(100% 0 0 / 0.08);
          color: white;
        }
        .dashboard-store-middle .dsm-grow-ghost {
          background: oklch(100% 0 0 / 0.08) !important;
          border: 1px solid oklch(100% 0 0 / 0.12) !important;
          color: white !important;
        }
        .dashboard-store-middle .dsm-grow-ghost:hover {
          background: oklch(100% 0 0 / 0.14) !important;
          border-color: oklch(100% 0 0 / 0.22) !important;
        }
        .dashboard-store-middle .dsm-ghost-btn-sm {
          padding: 6px 12px;
          background: var(--dsm-panel);
          border: 1px solid var(--dsm-line);
          border-radius: 8px;
          font-size: 12px;
          font-weight: 500;
          color: var(--dsm-ink);
          cursor: pointer;
          font-family: inherit;
        }
        .dashboard-store-middle .dsm-ghost-btn-sm:hover {
          background: var(--dsm-primary-soft);
          border-color: var(--dsm-primary);
          color: var(--dsm-primary);
        }
        .dashboard-store-middle .dsm-link-btn {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          background: none;
          border: none;
          padding: 0;
          font-size: 12px;
          font-weight: 500;
          color: var(--dsm-primary);
          cursor: pointer;
          font-family: inherit;
        }
        .dashboard-store-middle .dsm-link-btn:hover { text-decoration: underline; }
        .dashboard-store-middle .dsm-icon-btn {
          width: 28px;
          height: 28px;
          padding: 0;
          background: transparent;
          border: 1px solid transparent;
          border-radius: 8px;
          color: var(--dsm-ink-3);
          cursor: pointer;
          display: grid;
          place-items: center;
        }
        .dashboard-store-middle .dsm-icon-btn:hover {
          background: var(--dsm-bg);
          color: var(--dsm-ink);
        }
        .dashboard-store-middle .dsm-bottom-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
          gap: 14px;
          margin-top: 24px;
        }
        .dashboard-store-middle .dsm-bottom-grid--single {
          grid-template-columns: 1fr !important;
        }
        @media (max-width: 991px) {
          .dashboard-store-middle .dsm-actions-grid {
            grid-template-columns: 1fr !important;
          }
          .dashboard-store-middle .dsm-snapshot-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
          .dashboard-store-middle .dsm-bottom-grid {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 576px) {
          .dashboard-store-middle .dsm-snapshot-grid {
            grid-template-columns: 1fr !important;
          }
          .dashboard-store-middle .dsm-snapshot-grid > div {
            border-right: none !important;
            border-bottom: 1px solid var(--dsm-line-2);
          }
          .dashboard-store-middle .dsm-snapshot-grid > div:last-child {
            border-bottom: none;
          }
        }
        @keyframes dsm-fade-in {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <TodaySection density='comfy' />
      <ActionsSection
        onNavigateCampaign={() => navigate('/campaign')}
        onNavigateOffer={() => navigate('/e-store')}
        onNavigateAnalytics={() => navigate('/analytics')}
      />
      <CampaignsSection
        onViewCampaign={() => navigate('/campaign')}
        onViewAllCampaigns={() => navigate('/campaign')}
      />

      <div className={`dsm-bottom-grid${showGrow ? '' : ' dsm-bottom-grid--single'}`}>
        <SetupSection onFinishSetup={() => navigate('/products')} />
        {showGrow && (
          <GrowSection
            onNavigateHelp={() => navigate('/help')}
            onDismiss={() => setShowGrow(false)}
          />
        )}
      </div>
    </div>
  )
}
